import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import { LuArrowLeft } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { Role } from '@ndvr/auth-api';
import { AllocationType } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Page } from '@/components/ui/page';
import { useUserHasRole } from 'views/admin/dev-queries';

import { calculateTotalWeight } from '../../shared-utils';
import { useListStrategies } from '../../strategies/strategy-queries';
import { useCreateAllocationWithStrategies, useListAllocations } from '../allocation-queries';
import { createModelFormSchema, ModelFormValues } from './model-form-schema';
import { ModelFormBody } from './ModelFormBody';
import { toStrategyElementsRequest } from './toStrategyElementsRequest';
import { useModelStrategyElements } from './useModelStrategyElements';

export const NewModelPage: React.FC = () => {
    const navigate = useNavigate();
    const { data: hasSupportRole } = useUserHasRole(Role.SUPPORT_USER);
    const { data: allocations = [] } = useListAllocations();
    const { data: strategies = [] } = useListStrategies();
    const { mutate: createAllocationWithStrategies, isPending: isSaving } = useCreateAllocationWithStrategies();

    const formSchema = createModelFormSchema(
        allocations.map((a) => a.allocationName),
        strategies.map((s) => s.strategyName),
        strategies,
    );

    const form = useForm<ModelFormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: { allocationName: '', allocationType: AllocationType.PERSONAL, strategyElements: [] },
        mode: 'onSubmit',
    });

    const elements = form.watch('strategyElements');
    const { commitNew } = useModelStrategyElements(form);

    const handleBack = () => navigate('/model-library');

    const totalWeight = calculateTotalWeight(elements);
    const isNamed = form.watch('allocationName').trim().length > 0;
    const isValid = isNamed && elements.length >= 1 && totalWeight === 100;

    const validationMessage = !isNamed
        ? 'Name the model to save.'
        : elements.length < 1
          ? 'Add at least one strategy.'
          : totalWeight !== 100
            ? 'Strategy weights must total 100% to save.'
            : undefined;

    const handleSave = (data: ModelFormValues) => {
        createAllocationWithStrategies(
            {
                allocationName: data.allocationName,
                allocationType: data.allocationType,
                strategyElements: toStrategyElementsRequest(data.strategyElements),
            },
            { onSuccess: handleBack },
        );
    };

    return (
        <Page className="tracking-[-0.02em]">
            <div className="border-cocoa-200 sticky top-0 z-10 flex min-h-16 items-center gap-4 border-b bg-white px-8">
                <button
                    type="button"
                    onClick={handleBack}
                    className="text-cocoa-500 hover:text-cocoa-1000 flex items-center gap-1.5 text-sm font-medium"
                >
                    <LuArrowLeft className="h-4 w-4" /> Model Library
                </button>
                <span className="text-cocoa-300 text-sm">/</span>
                <span className="text-cocoa-700 text-sm">Model</span>
                <div className="ml-auto flex items-center gap-3">
                    {validationMessage && <span className="text-sm text-red-600">{validationMessage}</span>}
                    <Button
                        variant="outline"
                        type="button"
                        className="font-medium"
                        onClick={handleBack}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="default"
                        type="submit"
                        className="font-medium"
                        form="new-model-form"
                        disabled={!isValid || isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save model'}
                    </Button>
                </div>
            </div>

            <form id="new-model-form" onSubmit={form.handleSubmit(handleSave)} className="overflow-y-auto p-8 pb-24">
                <ModelFormBody
                    form={form}
                    showTypeSelector={!!hasSupportRole}
                    availableStrategies={strategies}
                    existingStrategyNames={strategies.map((s) => s.strategyName)}
                    onCommitNewStrategy={commitNew}
                    autoFocusName
                />
            </form>
        </Page>
    );
};
